
import React, { useEffect, useState } from "react";
import { TrendingUp, User, Briefcase } from "lucide-react";
import { BehavioralFactor, BehavioralScores, PersonalityProfile } from "@/types/resultsTypes";
import SectionHeader from "./personality-story/SectionHeader";
import DualAssessmentWarning from "./personality-story/DualAssessmentWarning";
import TraitDescriptorsBadges from "./personality-story/TraitDescriptorsBadges";
import { usePersonalityStoryData } from "./hooks/usePersonalityStoryData";
import { useIntersectionVisible } from "./hooks/useIntersectionVisible";
import BehavioralSection from "./personality-story/BehavioralSection";
import AIInsightsLoadingState from "./states/AIInsightsLoadingState";
import ErrorState from "./states/ErrorState";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";

interface TraitInsight {
  strength?: string;
  challenge?: string;
  modification?: string;
}

interface InsightsResponse {
  trait_insights?: TraitInsight[];
  selfInsights?: {
    trait_insights?: TraitInsight[];
    summary_points?: string[];
  };
  workInsights?: {
    trait_insights?: TraitInsight[];
    summary_points?: string[];
  };
  summary_points?: string[];
}

interface PersonalityStorySectionProps {
  behavioralFactors: BehavioralFactor[];
  objectivityFactor?: BehavioralFactor;
  scores: BehavioralScores;
  selectedWordsCount: number;
  isDualAssessment?: boolean;
  selfBehavioralFactors?: BehavioralFactor[] | null;
  workBehavioralFactors?: BehavioralFactor[] | null;
  selfWordsCount?: number;
  workWordsCount?: number;
  // AI Insights props
  personalityProfile?: PersonalityProfile;
  selectedWordsText?: string[];
  selfPersonalityProfile?: PersonalityProfile;
  workPersonalityProfile?: PersonalityProfile;
  selfSelectedWordsText?: string[];
  workSelectedWordsText?: string[];
  candidateLanguage?: string;
}

const PersonalityStorySection: React.FC<PersonalityStorySectionProps> = ({
  behavioralFactors,
  objectivityFactor,
  scores,
  selectedWordsCount,
  isDualAssessment = false,
  selfBehavioralFactors = null,
  workBehavioralFactors = null,
  selfWordsCount = 0,
  workWordsCount = 0,
  personalityProfile,
  selectedWordsText = [],
  selfPersonalityProfile,
  workPersonalityProfile,
  selfSelectedWordsText = [],
  workSelectedWordsText = [],
  candidateLanguage,
}) => {
  const { isVisible, ref } = useIntersectionVisible();
  const { language: workspaceLanguage, t } = useLanguage();
  const traitDescriptors = usePersonalityStoryData(behavioralFactors);
  
  const [insights, setInsights] = useState<InsightsResponse | null>(null);
  const [isLoadingInsights, setIsLoadingInsights] = useState(true);

  // Use candidate's assessment language if provided, otherwise fall back to workspace language
  const insightsLanguage = candidateLanguage || workspaceLanguage;

  // Safely handle dual arrays
  const safeSelfFactors = Array.isArray(selfBehavioralFactors)
    ? selfBehavioralFactors
    : [];
  const safeWorkFactors = Array.isArray(workBehavioralFactors)
    ? workBehavioralFactors
    : [];
  const selfHasGraph = safeSelfFactors.length > 0;
  const workHasGraph = safeWorkFactors.length > 0;
  const showDualGraphPartialWarning =
    isDualAssessment && (!selfHasGraph || !workHasGraph);

  const completeScores = {
    dominance: scores.dominance,
    extraversion: scores.extraversion,
    patience: scores.patience,
    formality: scores.formality,
    objectivity: scores.objectivity ?? scores.objectivityModel1 ?? scores.objectivityModel2 ?? 5
  };

  const generateInsights = async () => {
    setIsLoadingInsights(true);
    
    try {
      let body;
      
      const hasDualData = isDualAssessment && 
        safeSelfFactors.length > 0 &&
        safeWorkFactors.length > 0 &&
        selfPersonalityProfile?.type &&
        workPersonalityProfile?.type &&
        selfSelectedWordsText.length > 0 &&
        workSelectedWordsText.length > 0;
      
      if (hasDualData) {
        body = {
          isDualAssessment: true,
          selfData: {
            personalityProfile: selfPersonalityProfile,
            behavioralFactors: safeSelfFactors,
            selectedWords: selfSelectedWordsText
          },
          workData: {
            personalityProfile: workPersonalityProfile,
            behavioralFactors: safeWorkFactors,
            selectedWords: workSelectedWordsText
          },
          language: insightsLanguage
        };
      } else {
        body = {
          isDualAssessment: false,
          personalityProfile,
          scores: completeScores,
          selectedWords: selectedWordsText,
          behavioralFactors,
          language: insightsLanguage
        };
      }

      const { data, error } = await supabase.functions.invoke('generate-personality-insights', {
        body
      });

      if (error) throw error;

      if (isDualAssessment && data?.selfInsights && data?.workInsights) {
        setInsights(data);
      } else if (!isDualAssessment && (data?.trait_insights || data?.summary_points)) {
        setInsights(data);
      } else {
        setInsights(null);
      }
    } catch (error) {
      console.error('AI insights generation failed:', error);
      setInsights(null);
    } finally {
      setIsLoadingInsights(false);
    }
  };

  useEffect(() => {
    generateInsights();
  }, []);

  // Render trait insights (strength + challenge format)
  const renderTraitInsights = (traitInsights: TraitInsight[], borderColor: string) => (
    <div className="space-y-4 w-full">
      {traitInsights.map((insight, index) => (
        <div 
          key={index}
          className={`border-l-4 ${borderColor} pl-4 py-2 w-full`}
        >
          <p className="text-foreground leading-relaxed text-sm break-words">
            {insight.strength} {insight.challenge}
          </p>
        </div>
      ))}
    </div>
  );

  // Render modification insights (for Work Persona)
  const renderModificationInsights = (traitInsights: TraitInsight[], borderColor: string) => (
    <div className="space-y-4 w-full">
      {traitInsights.map((insight, index) => (
        <div 
          key={index}
          className={`border-l-4 ${borderColor} pl-4 py-2 w-full`}
        >
          <p className="text-foreground leading-relaxed text-sm break-words">
            {insight.modification}
          </p>
        </div>
      ))}
    </div>
  );

  // Render legacy summary points
  const renderSummaryPoints = (points: string[], borderColor: string) => (
    <div className="space-y-4 w-full">
      {points.map((point, index) => (
        <div 
          key={index}
          className={`border-l-4 ${borderColor} pl-4 py-2 w-full`}
        >
          <p className="text-foreground leading-relaxed text-sm break-words">{point}</p>
        </div>
      ))}
    </div>
  );

  // Render AI insights section
  const renderAIInsights = () => {
    if (isLoadingInsights) {
      return <AIInsightsLoadingState isDualAssessment={isDualAssessment} />;
    }

    if (!insights) {
      return (
        <ErrorState 
          message="We couldn't generate insights right now."
          actionLabel="Retry"
          onAction={generateInsights}
        />
      );
    }

    if (isDualAssessment && insights.selfInsights && insights.workInsights) {
      return null; // Dual insights rendered inline with each chart
    }

    return (
      <div className="border-t border-border/50 pt-6 mt-6">
        <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-4">
          {t('results.aiInsights.aiPoweredAnalysis')}
        </h4>
        {insights.trait_insights 
          ? renderTraitInsights(insights.trait_insights, 'border-foreground')
          : insights.summary_points 
            ? renderSummaryPoints(insights.summary_points, 'border-foreground')
            : null
        }
      </div>
    );
  };

  // Render dual assessment insights for a specific section (no loading state - handled externally)
  const renderDualInsights = (type: 'self' | 'work') => {
    const sectionInsights = type === 'self' ? insights?.selfInsights : insights?.workInsights;
    if (!sectionInsights) return null;

    const borderColor = type === 'self' ? 'border-foreground' : 'border-primary';
    const title = type === 'self' ? t('results.personalityStory.naturalSelf') : t('results.personalityStory.workPersona');
    const Icon = type === 'self' ? User : Briefcase;

    return (
      <div className="border-t border-border/50 pt-6 mt-4">
        <div className="flex items-center gap-2 mb-4">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">{title}</h4>
        </div>
        {sectionInsights.trait_insights 
          ? (type === 'work' 
              ? renderModificationInsights(sectionInsights.trait_insights, borderColor)
              : renderTraitInsights(sectionInsights.trait_insights, borderColor))
          : sectionInsights.summary_points 
            ? renderSummaryPoints(sectionInsights.summary_points, borderColor)
            : null
        }
      </div>
    );
  };

  return (
    <div
      id="personality-story"
      ref={ref}
      className="min-h-screen bg-background py-20 px-4 sm:px-6 w-full overflow-x-hidden"
    >
      <div className="max-w-6xl mx-auto w-full">
        <div
          className={`transition-all duration-700 ${
            isVisible ? "opacity-100" : "opacity-0"
          }`}
        >
          {/* Content Container */}
          <div className="w-full">
            <SectionHeader
              isDualAssessment={isDualAssessment}
              selectedWordsCount={selectedWordsCount}
              selfWordsCount={selfWordsCount}
              workWordsCount={workWordsCount}
            />
            <DualAssessmentWarning
              show={showDualGraphPartialWarning}
              selfHasGraph={selfHasGraph}
              workHasGraph={workHasGraph}
            />
          {isDualAssessment ? (
              <div className="w-full">
                <div className="grid md:grid-cols-2 gap-6 sm:gap-8 w-full">
                  <BehavioralSection
                    factors={objectivityFactor ? [...safeSelfFactors, objectivityFactor] : safeSelfFactors}
                    title={t('results.personalityStory.selfAssessment')}
                    icon={<User className="h-4 w-4 mr-2" />}
                    wordsCount={selfWordsCount}
                    isVisible={isVisible}
                    showChart={true}
                    showKeyInsights={false}
                  />
                  <BehavioralSection
                    factors={objectivityFactor ? [...safeWorkFactors, objectivityFactor] : safeWorkFactors}
                    title={t('results.personalityStory.workBehavior')}
                    icon={<Briefcase className="h-4 w-4 mr-2" />}
                    wordsCount={workWordsCount}
                    isVisible={isVisible}
                    showChart={true}
                    showKeyInsights={false}
                  />
                </div>
                
                {/* Single loading/insights section below both charts */}
                {isLoadingInsights ? (
                  <AIInsightsLoadingState isDualAssessment={true} />
                ) : insights?.selfInsights && insights?.workInsights ? (
                  <div className="grid md:grid-cols-2 gap-6 sm:gap-8 w-full mt-6">
                    <div>{renderDualInsights('self')}</div>
                    <div>{renderDualInsights('work')}</div>
                  </div>
                ) : (
                  <ErrorState 
                    message="We couldn't generate insights right now."
                    actionLabel="Retry"
                    onAction={generateInsights}
                  />
                )}
              </div>
            ) : (
              <div className="w-full">
                <TraitDescriptorsBadges
                  traitDescriptors={traitDescriptors}
                  isVisible={isVisible}
                />
                <BehavioralSection
                  factors={objectivityFactor ? [...behavioralFactors, objectivityFactor] : behavioralFactors}
                  title={t('results.personalityStory.yourBehavioralProfile')}
                  icon={<TrendingUp className="h-4 w-4 mr-2" />}
                  wordsCount={selectedWordsCount}
                  isVisible={isVisible}
                  showChart={true}
                  showKeyInsights={false}
                />
                {renderAIInsights()}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalityStorySection;
